import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';
import AddCardModal, { formatStripeError } from './AddCardModal';

function PaymentMethods() {
  const { token } = useAuth();
  const [savedCard, setSavedCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const fetchPaymentMethods = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/stripe/payment-methods`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load payment method');
      }

      setSavedCard(data.paymentMethods?.[0] || null);
    } catch (err) {
      setError(formatStripeError(err));
      setSavedCard(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const handleRemoveConfirm = async () => {
    if (!savedCard) return;

    try {
      setActionLoading(true);
      const response = await fetch(`${API_URL}/stripe/payment-methods/${savedCard.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove card');
      }

      setShowRemoveConfirm(false);
      await fetchPaymentMethods();
    } catch (err) {
      setError(formatStripeError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const formatBrand = (brand) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  return (
    <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8 shadow-xl w-full">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold text-xl lg:text-2xl text-white">Payment Method</h3>
        <button
          type="button"
          onClick={() => setShowAddCardModal(true)}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-accent-blue/10 border border-accent-blue/30 text-accent-blue hover:bg-accent-blue/20"
        >
          {savedCard ? 'Replace card' : 'Add card'}
        </button>
      </div>

      <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06]">
        <svg
          className="w-3.5 h-3.5 text-white/40 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span className="text-white/40 text-xs">Secured by Stripe</span>
      </div>

      {error && (
        <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-white/50 text-sm">Loading...</p>
      ) : !savedCard ? (
        <p className="text-white/50 text-sm">No card on file.</p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-2xl border border-dark-border bg-dark-bg/30">
          <div>
            <div className="text-white font-semibold">
              {formatBrand(savedCard.brand)} ending in {savedCard.last4}
            </div>
            <div className="text-white/50 text-sm">
              Expires {savedCard.expMonth}/{savedCard.expYear}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowRemoveConfirm(true)}
            disabled={actionLoading}
            className="px-3 py-2 rounded-lg text-xs font-medium border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      )}

      <AddCardModal
        isOpen={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        onSuccess={fetchPaymentMethods}
        title={savedCard ? 'Replace card' : 'Add a card'}
        description={
          savedCard
            ? 'Your new card replaces the one on file. Only one card is saved per account.'
            : 'Your card is saved to your account and used for purchases.'
        }
      />

      {showRemoveConfirm && savedCard && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-5"
          onClick={() => !actionLoading && setShowRemoveConfirm(false)}
        >
          <div
            className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Remove card?</h3>
            <p className="text-white/60 text-sm md:text-base mb-6">
              {formatBrand(savedCard.brand)} ending in {savedCard.last4} will be removed from your
              account. You can add a new card anytime.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowRemoveConfirm(false)}
                disabled={actionLoading}
                className="flex-1 py-3 px-6 rounded-xl font-semibold text-base transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 hover:border-white/20 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRemoveConfirm}
                disabled={actionLoading}
                className="flex-1 py-3 px-6 rounded-xl font-semibold text-base transition-all duration-200 bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Removing...' : 'Remove card'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentMethods;
